import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { wahaClient } from '@/services/waha-client';
import {
  WATextMessage,
  WAMediaMessage,
  WALocationMessage,
  WAButtonMessage,
  WAPollMessage,
  WAMessageResponse,
  WAForwardMessage,
  WATypingRequest,
} from '@/services/interface';

/**
 * Hook for WhatsApp messaging operations
 */
export function useWhatsappMessages(sessionName: string = 'default') {
  const queryClient = useQueryClient();

  // == MUTATIONS ==

  // Send text message
  const sendTextMessageMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WATextMessage, 'session'>
  >({
    mutationFn: async (messageData) => {
      const data = { ...messageData, session: sessionName };
      return await wahaClient.post<WAMessageResponse>('/api/sendText', data);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Send media message (image, audio, document, etc)
  const sendMediaMessageMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WAMediaMessage, 'session'>
  >({
    mutationFn: async (messageData) => {
      const data = { ...messageData, session: sessionName };

      // Use appropriate endpoint based on mimetype
      let endpoint = '/api/sendFile';
      if (messageData.file.mimetype.startsWith('image/')) {
        endpoint = '/api/sendImage';
      } else if (messageData.file.mimetype.startsWith('audio/')) {
        endpoint = '/api/sendVoice';
      } else if (messageData.file.mimetype.startsWith('video/')) {
        endpoint = '/api/sendVideo';
      }

      return await wahaClient.post<WAMessageResponse>(endpoint, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Send location
  const sendLocationMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WALocationMessage, 'session'>
  >({
    mutationFn: async (locationData) => {
      const data = { ...locationData, session: sessionName };
      return await wahaClient.post<WAMessageResponse>(
        '/api/sendLocation',
        data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Send buttons
  const sendButtonsMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WAButtonMessage, 'session'>
  >({
    mutationFn: async (buttonData) => {
      const data = { ...buttonData, session: sessionName };
      return await wahaClient.post<WAMessageResponse>('/api/sendButtons', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Send polls
  const sendPollMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WAPollMessage, 'session'>
  >({
    mutationFn: async (pollData) => {
      const data = { ...pollData, session: sessionName };
      return await wahaClient.post<WAMessageResponse>('/api/sendPoll', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Forward messages
  const forwardMessageMutation = useMutation<
    WAMessageResponse,
    Error,
    Omit<WAForwardMessage, 'session'>
  >({
    mutationFn: async (forwardData) => {
      const data = { ...forwardData, session: sessionName };
      return await wahaClient.post<WAMessageResponse>(
        '/api/forwardMessage',
        data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Mark message as seen
  const markSeenMutation = useMutation<void, Error, { chatId: string }>({
    mutationFn: async ({ chatId }) => {
      await wahaClient.post('/api/sendSeen', { chatId, session: sessionName });
    },
  });

  // Start typing indicator
  const startTypingMutation = useMutation<
    void,
    Error,
    Omit<WATypingRequest, 'session'>
  >({
    mutationFn: async (typingData) => {
      const data = { ...typingData, session: sessionName };
      await wahaClient.post('/api/startTyping', data);
    },
  });

  // Stop typing indicator
  const stopTypingMutation = useMutation<
    void,
    Error,
    Omit<WATypingRequest, 'session'>
  >({
    mutationFn: async (typingData) => {
      const data = { ...typingData, session: sessionName };
      await wahaClient.post('/api/stopTyping', data);
    },
  });

  // == QUERIES ==

  // Get recent messages from a chat
  const useRecentMessages = (chatId: string, limit: number = 50) => {
    return useQuery({
      queryKey: ['messages', sessionName, chatId],
      queryFn: async () => {
        const response = await wahaClient.get<WAMessageResponse[]>(
          `/api/messages?chatId=${chatId}&limit=${limit}&session=${sessionName}`,
        );
        return response;
      },
      staleTime: 10 * 1000, // 10 seconds
    });
  };

  return {
    // Text messaging
    sendTextMessage: sendTextMessageMutation.mutateAsync,
    isSendingText: sendTextMessageMutation.isPending,

    // Media messaging
    sendMediaMessage: sendMediaMessageMutation.mutateAsync,
    isSendingMedia: sendMediaMessageMutation.isPending,

    // Location
    sendLocation: sendLocationMutation.mutateAsync,
    isSendingLocation: sendLocationMutation.isPending,

    // Interactive messaging
    sendButtons: sendButtonsMutation.mutateAsync,
    isSendingButtons: sendButtonsMutation.isPending,
    sendPoll: sendPollMutation.mutateAsync,
    isSendingPoll: sendPollMutation.isPending,

    // Message management
    forwardMessage: forwardMessageMutation.mutateAsync,
    isForwarding: forwardMessageMutation.isPending,
    markSeen: markSeenMutation.mutateAsync,
    isMarkingSeen: markSeenMutation.isPending,

    // Typing indicators
    startTyping: startTypingMutation.mutateAsync,
    stopTyping: stopTypingMutation.mutateAsync,
    isTyping: startTypingMutation.isPending || stopTypingMutation.isPending,

    // Queries
    useRecentMessages,
  };
}
